<!--
 * Path: src/core/components/dialogs/RegisterDialog.vue
 * Task: Handles user registration in an isolated, single-purpose dialog panel.
-->
<template>
  <BaseDialog
    v-model="authStore.isRegisterDialogOpen"
    title="Create Account"
    max-width="480"
    @close="authStore.isRegisterDialogOpen = false"
  >
    <!-- pt-2 added to prevent the outlined floating label from being clipped -->
    <v-form @submit.prevent="handleRegister" @keydown.enter="handleRegister" class="pt-2">
      <v-text-field
        ref="registerNameRef"
        v-model="authData.name"
        label="Full Name"
        prepend-inner-icon="mdi-account"
        variant="outlined"
        density="comfortable"
        class="mb-2"
      ></v-text-field>
      <v-text-field
        v-model="authData.email"
        label="Email Address"
        prepend-inner-icon="mdi-email"
        variant="outlined"
        density="comfortable"
        class="mb-2"
      ></v-text-field>
      <v-text-field
        v-model="authData.password"
        label="Password"
        prepend-inner-icon="mdi-lock"
        type="password"
        variant="outlined"
        density="comfortable"
      ></v-text-field>
    </v-form>

    <div class="text-body-2 text-medium-emphasis mt-2 px-1">
      Already have an account?
      <a href="#" class="text-primary font-weight-bold text-decoration-none" @click.prevent="switchToLogin">
        Log in here
      </a>
    </div>

    <!-- Footer action controls -->
    <template #actions>
      <v-btn variant="text" @click="authStore.isRegisterDialogOpen = false">Cancel</v-btn>
      <v-spacer></v-spacer>
      <v-btn
        color="primary"
        variant="flat"
        @click="handleRegister"
        :loading="authStore.isLoading"
      >
        Create Account
      </v-btn>
    </template>
  </BaseDialog>
</template>

<script setup lang="ts">
import { ref, watch, nextTick } from 'vue';
import { useAuthStore } from '@/store/auth';
import BaseDialog from './BaseDialog.vue';

const authStore = useAuthStore();

const registerNameRef = ref<any>(null);

const authData = ref({
  email: '',
  password: '',
  name: ''
});

const focusField = (): void => {
  nextTick(() => {
    if (registerNameRef.value) {
      const el = registerNameRef.value.$el?.querySelector('input');
      el?.focus();
    }
  });
};

watch(() => authStore.isRegisterDialogOpen, (isOpen) => {
  if (isOpen) {
    focusField();
  }
});

const switchToLogin = () => {
  authStore.isRegisterDialogOpen = false;
  authStore.isLoginDialogOpen = true;
};

const handleRegister = async (): Promise<void> => {
  try {
    await authStore.register({
      email: authData.value.email,
      password: authData.value.password,
      name: authData.value.name
    });
    authData.value.password = '';
    authStore.isRegisterDialogOpen = false;
    authStore.showNotification('Registration successful! Please log in.', 'success');

    // Automatically switch to login screen after successful registration
    setTimeout(() => {
      authStore.isLoginDialogOpen = true;
    }, 300);
  } catch (error: any) {
    authStore.showNotification(error.message || 'Registration failed.', 'error');
  }
};
</script>
